// PhonologicalFeatureSystem.ts: Distinctive features, natural classes, and phonological rules
// Implements classical generative phonology framework

export interface DistinctiveFeatureMatrix {
    // Major class features
    consonantal: boolean;    // [+consonantal] for consonants
    sonorant: boolean;       // [+sonorant] for nasals, liquids, glides, vowels
    syllabic: boolean;       // [+syllabic] for vowels
    
    // Manner features
    continuant: boolean;     // [+continuant] for fricatives vs stops
    strident: boolean;       // [+strident] for sibilants
    lateral: boolean;        // [+lateral] for laterals
    nasal: boolean;          // [+nasal] for nasals
    
    // Laryngeal features
    voicing: boolean;        // [+voice] for voiced sounds
    
    // Place features
    anterior: boolean;       // [+anterior] for labials, dentals, alveolars
    coronal: boolean;        // [+coronal] for coronals (dental, alveolar, palatal)
    distributed: boolean;    // [+distributed] for spread articulation (f, θ)
    high: boolean;           // [+high] for high vowels/consonants (velars, palatals)
    low: boolean;            // [+low] for low vowels
    back: boolean;           // [+back] for back vowels/consonants
    round: boolean;          // [+round] for rounded vowels
    
    // Additional features
    tense: boolean;          // [+tense] for tense vowels
    long: boolean;           // [+long] for long vowels/cons
}

export interface NaturalClass {
    name: string;
    description: string;
    features: Partial<DistinctiveFeatureMatrix>;
    soundClass: 'consonant' | 'vowel' | 'both';
    members: Set<string>;
}

export interface PhonologicalRule {
    name: string;
    description: string;
    input: Partial<DistinctiveFeatureMatrix>;
    output: Partial<DistinctiveFeatureMatrix>;
    environment: PhonologicalEnvironment;
    naturalClass?: string;
    obligatory: boolean;
}

export interface PhonologicalEnvironment {
    leftContext: string;      // e.g., "#" for word boundary, "+" for morpheme boundary
    rightContext: string;
    structuralDescription: string;  // Full environment notation
}

export class PhonologicalFeatureSystem {
    private featureMatrices: Map<string, DistinctiveFeatureMatrix> = new Map();
    private naturalClasses: Map<string, NaturalClass> = new Map();
    private phonologicalRules: PhonologicalRule[] = [];
    
    constructor() {
        this.initializeFeatureMatrices();
        this.initializeNaturalClasses();
        this.initializePhonologicalRules();
    }

    private initializeFeatureMatrices(): void {
        // Vowels
        this.setupVowels();
        // Consonants
        this.setupConsonants();
    }

    private setupVowels(): void {
        // High front unrounded vowels (i, ɪ)
        this.featureMatrices.set('i', {
            consonantal: false, sonorant: true, syllabic: true,
            continuant: true, strident: false, lateral: false, nasal: false,
            voicing: true, anterior: false, coronal: false, distributed: false,
            high: true, low: false, back: false, round: false, tense: true, long: false
        });
        
        this.featureMatrices.set('ɪ', {
            consonantal: false, sonorant: true, syllabic: true,
            continuant: true, strident: false, lateral: false, nasal: false,
            voicing: true, anterior: false, coronal: false, distributed: false,
            high: true, low: false, back: false, round: false, tense: false, long: false
        });
        
        // High front rounded vowels (y, ʏ)
        this.featureMatrices.set('y', {
            consonantal: false, sonorant: true, syllabic: true,
            continuant: true, strident: false, lateral: false, nasal: false,
            voicing: true, anterior: false, coronal: false, distributed: false,
            high: true, low: false, back: false, round: true, tense: true, long: false
        });
        
        // Mid vowels (e, ɛ, ə, ɜ, o, ɔ, ʌ)
        ['e', 'ɛ', 'ə', 'ɜ', 'o', 'ɔ', 'ʌ'].forEach(symbol => {
            this.featureMatrices.set(symbol, {
                consonantal: false, sonorant: true, syllabic: true,
                continuant: true, strident: false, lateral: false, nasal: false,
                voicing: true, anterior: false, coronal: false, distributed: false,
                high: false, low: false, back: symbol === 'o' || symbol === 'ɔ' || symbol === 'ʌ',
                round: symbol === 'o' || symbol === 'ɔ', tense: true, long: false
            });
        });
        
        // Low vowels (a, ɑ, æ)
        ['a', 'ɑ', 'æ'].forEach(symbol => {
            this.featureMatrices.set(symbol, {
                consonantal: false, sonorant: true, syllabic: true,
                continuant: true, strident: false, lateral: false, nasal: false,
                voicing: true, anterior: false, coronal: false, distributed: false,
                high: false, low: true, back: symbol === 'ɑ',
                round: symbol === 'ɑ', tense: true, long: false
            });
        });
    }

    private setupConsonants(): void {
        // Stops: p, b, t, d, k, g
        this.featureMatrices.set('p', {
            consonantal: true, sonorant: false, syllabic: false,
            continuant: false, strident: false, lateral: false, nasal: false,
            voicing: false, anterior: true, coronal: false, distributed: true,
            high: false, low: false, back: false, round: false, tense: false, long: false
        });
        
        this.featureMatrices.set('b', {
            consonantal: true, sonorant: false, syllabic: false,
            continuant: false, strident: false, lateral: false, nasal: false,
            voicing: true, anterior: true, coronal: false, distributed: true,
            high: false, low: false, back: false, round: false, tense: false, long: false
        });
        
        this.featureMatrices.set('t', {
            consonantal: true, sonorant: false, syllabic: false,
            continuant: false, strident: false, lateral: false, nasal: false,
            voicing: false, anterior: true, coronal: true, distributed: true,
            high: false, low: false, back: false, round: false, tense: false, long: false
        });
        
        this.featureMatrices.set('d', {
            consonantal: true, sonorant: false, syllabic: false,
            continuant: false, strident: false, lateral: false, nasal: false,
            voicing: true, anterior: true, coronal: true, distributed: true,
            high: false, low: false, back: false, round: false, tense: false, long: false
        });
        
        this.featureMatrices.set('k', {
            consonantal: true, sonorant: false, syllabic: false,
            continuant: false, strident: false, lateral: false, nasal: false,
            voicing: false, anterior: false, coronal: false, distributed: false,
            high: true, low: false, back: true, round: false, tense: false, long: false
        });
        
        this.featureMatrices.set('g', {
            consonantal: true, sonorant: false, syllabic: false,
            continuant: false, strident: false, lateral: false, nasal: false,
            voicing: true, anterior: false, coronal: false, distributed: false,
            high: true, low: false, back: true, round: false, tense: false, long: false
        });
        
        // Fricatives: f, v, θ, ð, s, z, ʃ, ʒ, x, ɣ, h
        this.featureMatrices.set('f', {
            consonantal: true, sonorant: false, syllabic: false,
            continuant: true, strident: false, lateral: false, nasal: false,
            voicing: false, anterior: true, coronal: false, distributed: true,
            high: false, low: false, back: false, round: false, tense: false, long: false
        });
        
        this.featureMatrices.set('v', {
            consonantal: true, sonorant: false, syllabic: false,
            continuant: true, strident: false, lateral: false, nasal: false,
            voicing: true, anterior: true, coronal: false, distributed: true,
            high: false, low: false, back: false, round: false, tense: false, long: false
        });
        
        this.featureMatrices.set('s', {
            consonantal: true, sonorant: false, syllabic: false,
            continuant: true, strident: true, lateral: false, nasal: false,
            voicing: false, anterior: true, coronal: true, distributed: true,
            high: false, low: false, back: false, round: false, tense: false, long: false
        });
        
        this.featureMatrices.set('z', {
            consonantal: true, sonorant: false, syllabic: false,
            continuant: true, strident: true, lateral: false, nasal: false,
            voicing: true, anterior: true, coronal: true, distributed: true,
            high: false, low: false, back: false, round: false, tense: false, long: false
        });
        
        this.featureMatrices.set('ʃ', {
            consonantal: true, sonorant: false, syllabic: false,
            continuant: true, strident: true, lateral: false, nasal: false,
            voicing: false, anterior: false, coronal: true, distributed: false,
            high: true, low: false, back: false, round: false, tense: false, long: false
        });
        
        this.featureMatrices.set('ʒ', {
            consonantal: true, sonorant: false, syllabic: false,
            continuant: true, strident: true, lateral: false, nasal: false,
            voicing: true, anterior: false, coronal: true, distributed: false,
            high: true, low: false, back: false, round: false, tense: false, long: false
        });
        
        // Nasals: m, n, ŋ
        this.featureMatrices.set('m', {
            consonantal: true, sonorant: true, syllabic: false,
            continuant: false, strident: false, lateral: false, nasal: true,
            voicing: true, anterior: true, coronal: false, distributed: true,
            high: false, low: false, back: false, round: false, tense: false, long: false
        });
        
        this.featureMatrices.set('n', {
            consonantal: true, sonorant: true, syllabic: false,
            continuant: false, strident: false, lateral: false, nasal: true,
            voicing: true, anterior: true, coronal: true, distributed: true,
            high: false, low: false, back: false, round: false, tense: false, long: false
        });
        
        this.featureMatrices.set('ŋ', {
            consonantal: true, sonorant: true, syllabic: false,
            continuant: false, strident: false, lateral: false, nasal: true,
            voicing: true, anterior: false, coronal: false, distributed: false,
            high: true, low: false, back: true, round: false, tense: false, long: false
        });
        
        // Liquids: l, ɹ
        this.featureMatrices.set('l', {
            consonantal: true, sonorant: true, syllabic: false,
            continuant: true, strident: false, lateral: true, nasal: false,
            voicing: true, anterior: true, coronal: true, distributed: true,
            high: false, low: false, back: false, round: false, tense: false, long: false
        });
        
        this.featureMatrices.set('ɹ', {
            consonantal: true, sonorant: true, syllabic: false,
            continuant: true, strident: false, lateral: false, nasal: false,
            voicing: true, anterior: true, coronal: true, distributed: false,
            high: false, low: false, back: false, round: false, tense: false, long: false
        });
        
        // Glides: j, w
        this.featureMatrices.set('j', {
            consonantal: false, sonorant: true, syllabic: false,
            continuant: true, strident: false, lateral: false, nasal: false,
            voicing: true, anterior: false, coronal: false, distributed: false,
            high: true, low: false, back: false, round: false, tense: false, long: false
        });
        
        this.featureMatrices.set('w', {
            consonantal: false, sonorant: true, syllabic: false,
            continuant: true, strident: false, lateral: false, nasal: false,
            voicing: true, anterior: true, coronal: false, distributed: true,
            high: true, low: false, back: true, round: true, tense: false, long: false
        });
    }

    private initializeNaturalClasses(): void {
        // Sonorants (nasals, liquids, glides, vowels)
        this.naturalClasses.set('sonorants', {
            name: 'Sonorants',
            description: 'Sounds produced with vocal fold vibration and airflow',
            features: { sonorant: true },
            soundClass: 'both',
            members: new Set(['m', 'n', 'ŋ', 'l', 'ɹ', 'j', 'w', 'i', 'ɪ', 'e', 'ɛ', 'a', 'o', 'ɔ', 'u'])
        });
        
        // Obstruents (stops and fricatives)
        this.naturalClasses.set('obstruents', {
            name: 'Obstruents',
            description: 'Consonants produced with obstruction of airflow',
            features: { sonorant: false, consonantal: true },
            soundClass: 'consonant',
            members: new Set(['p', 'b', 't', 'd', 'k', 'g', 'f', 'v', 's', 'z', 'ʃ', 'ʒ', 'θ', 'ð'])
        });
        
        // Stops
        this.naturalClasses.set('stops', {
            name: 'Stops',
            description: 'Consonants produced with complete obstruction',
            features: { continuant: false, consonantal: true },
            soundClass: 'consonant',
            members: new Set(['p', 'b', 't', 'd', 'k', 'g'])
        });
        
        // Fricatives
        this.naturalClasses.set('fricatives', {
            name: 'Fricatives',
            description: 'Consonants produced with partial obstruction',
            features: { continuant: true, consonantal: true },
            soundClass: 'consonant',
            members: new Set(['f', 'v', 's', 'z', 'ʃ', 'ʒ', 'θ', 'ð', 'x', 'ɣ'])
        });
        
        // Sibilants
        this.naturalClasses.set('sibilants', {
            name: 'Sibilants',
            description: 'High-amplitude fricatives',
            features: { strident: true },
            soundClass: 'consonant',
            members: new Set(['s', 'z', 'ʃ', 'ʒ'])
        });
        
        // High vowels
        this.naturalClasses.set('high_vowels', {
            name: 'High Vowels',
            description: 'Vowels with high tongue position',
            features: { high: true, syllabic: true },
            soundClass: 'vowel',
            members: new Set(['i', 'ɪ', 'y', 'u', 'ʊ'])
        });
        
        // Low vowels
        this.naturalClasses.set('low_vowels', {
            name: 'Low Vowels',
            description: 'Vowels with low tongue position',
            features: { low: true, syllabic: true },
            soundClass: 'vowel',
            members: new Set(['a', 'ɑ', 'æ'])
        });
        
        // Rounded vowels
        this.naturalClasses.set('rounded_vowels', {
            name: 'Rounded Vowels',
            description: 'Vowels with rounded lips',
            features: { round: true, syllabic: true },
            soundClass: 'vowel',
            members: new Set(['y', 'o', 'ɔ', 'u'])
        });
        
        // Voiced sounds
        this.naturalClasses.set('voiced', {
            name: 'Voiced',
            description: 'Sounds produced with vocal fold vibration',
            features: { voicing: true },
            soundClass: 'both',
            members: new Set(['b', 'd', 'g', 'v', 'z', 'ʒ', 'm', 'n', 'ŋ', 'l', 'ɹ', 'j', 'w', 'i', 'e', 'a', 'o', 'u'])
        });
        
        // Voiceless sounds
        this.naturalClasses.set('voiceless', {
            name: 'Voiceless',
            description: 'Sounds produced without vocal fold vibration',
            features: { voicing: false },
            soundClass: 'both',
            members: new Set(['p', 't', 'k', 'f', 's', 'ʃ', 'θ'])
        });
    }

    private initializePhonologicalRules(): void {
        // Voicing assimilation (s → z / __ V[+voice])
        this.phonologicalRules.push({
            name: 'voicing_assimilation',
            description: 'Voiceless sounds become voiced before voiced sounds',
            input: { voicing: false, consonantal: true },
            output: { voicing: true },
            environment: {
                leftContext: '',
                rightContext: '[+voice]',
                structuralDescription: 'Before voiced sound'
            },
            obligatory: false
        });
        
        // Place assimilation (n → ŋ / __ velar)
        this.phonologicalRules.push({
            name: 'place_assimilation',
            description: 'Nasals assimilate to place of following consonant',
            input: { nasal: true, anterior: true, coronal: true },
            output: { anterior: false, coronal: false, back: true },
            environment: {
                leftContext: '',
                rightContext: '[+back, +high]',
                structuralDescription: 'Before velar consonant'
            },
            naturalClass: 'nasals',
            obligatory: true
        });
        
        // Deletion of final obstruents
        this.phonologicalRules.push({
            name: 'final_deletion',
            description: 'Final voiceless obstruents delete',
            input: { voicing: false, consonantal: true },
            output: {},
            environment: {
                leftContext: '',
                rightContext: '#',
                structuralDescription: 'Word-finally'
            },
            obligatory: false
        });
    }

    // Get distinctive feature matrix for a sound
    public getFeatures(sound: string): DistinctiveFeatureMatrix | undefined {
        return this.featureMatrices.get(sound);
    }

    // Check if sound belongs to natural class
    public belongsToClass(sound: string, className: string): boolean {
        const features = this.featureMatrices.get(sound);
        const naturalClass = this.naturalClasses.get(className);
        
        if (!features || !naturalClass) return false;
        
        // Check if all features match
        for (const [key, value] of Object.entries(naturalClass.features)) {
            if (value !== undefined && features[key as keyof DistinctiveFeatureMatrix] !== value) {
                return false;
            }
        }
        
        return true;
    }

    // Find sounds that match a feature specification
    public findSoundsByFeatures(spec: Partial<DistinctiveFeatureMatrix>): string[] {
        const results: string[] = [];
        
        for (const [sound, features] of this.featureMatrices) {
            let matches = true;
            
            for (const [key, value] of Object.entries(spec)) {
                if (value !== undefined && features[key as keyof DistinctiveFeatureMatrix] !== value) {
                    matches = false;
                    break;
                }
            }
            
            if (matches) {
                results.push(sound);
            }
        }
        
        return results;
    }

    // Apply phonological rules to a string
    public applyRules(input: string, ruleNames?: string[]): string {
        let result = input;
        
        const rules = ruleNames 
            ? this.phonologicalRules.filter(r => ruleNames.includes(r.name))
            : this.phonologicalRules;
        
        for (const rule of rules) {
            // Simple pattern matching (would need proper implementation for full phonological derivation)
            // This is a simplified version
            if (rule.environment.rightContext === '#') {
                // Word-final rule
                result = result.replace(/(.)$/, (match, char) => {
                    const features = this.featureMatrices.get(char);
                    if (features && this.matchesInput(rule.input, features)) {
                        return this.applyOutput(char, rule.output);
                    }
                    return match;
                });
            }
        }
        
        return result;
    }

    private matchesInput(inputSpec: Partial<DistinctiveFeatureMatrix>, features: DistinctiveFeatureMatrix): boolean {
        for (const [key, value] of Object.entries(inputSpec)) {
            if (value !== undefined && features[key as keyof DistinctiveFeatureMatrix] !== value) {
                return false;
            }
        }
        return true;
    }

    private applyOutput(input: string, outputSpec: Partial<DistinctiveFeatureMatrix>): string {
        // Find candidate sound that best matches applying outputSpec to input's features
        const current = this.featureMatrices.get(input);
        if (!current) return input;

        // Build target features by overriding specified fields
        const target: DistinctiveFeatureMatrix = { ...current } as DistinctiveFeatureMatrix;
        for (const [key, value] of Object.entries(outputSpec)) {
            if (value !== undefined) {
                (target as any)[key] = value;
            }
        }

        // Score all known sounds and pick the closest to target features
        let bestSymbol = input;
        let bestScore = -Infinity;

        for (const [symbol, features] of this.featureMatrices.entries()) {
            const score = this.featureMatchScore(features, target);
            if (score > bestScore) {
                bestScore = score;
                bestSymbol = symbol;
            }
        }

        return bestSymbol;
    }

    // Compute similarity score between two feature matrices
    private featureMatchScore(a: DistinctiveFeatureMatrix, b: DistinctiveFeatureMatrix): number {
        let score = 0;
        for (const key of Object.keys(a) as (keyof DistinctiveFeatureMatrix)[]) {
            score += a[key] === b[key] ? 1 : 0;
        }
        return score;
    }

    // Get all natural classes
    public getNaturalClasses(): Map<string, NaturalClass> {
        return this.naturalClasses;
    }

    // Get phonological rules
    public getPhonologicalRules(): PhonologicalRule[] {
        return this.phonologicalRules;
    }
}

